import {
	AfterContentInit,
	ChangeDetectionStrategy,
	Component,
	ContentChildren,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	Output,
	QueryList,
	TemplateRef
} from "@angular/core";
import { PageChangedEvent } from "ngx-bootstrap/pagination";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { Filter } from "@data/schemas/filter/filter";
import { Restriction } from "@data/schemas/filter/restriction";
import { AutomataTemplateDirective } from "@shared/directives/automata-template.directive";
import { ColumnTable } from "@shared/schemas/column-table";
import { HttpParamsConfig } from "@shared/schemas/http-params-config";
import { RowTable } from "@shared/schemas/row-table";
import { TableCheckboxService } from "@shared/services/table-checkbox.service";
import { DatatableTemplate, RowExpandMode, SortedField } from "./types";
import { DataListNgSelect } from "@shared/schemas/data-list-ng-select";
import { BsDatepickerConfig, BsLocaleService } from "ngx-bootstrap/datepicker";

@Component({
	selector: "app-datatable",
	templateUrl: "./datatable.component.html",
	styleUrls: ["./datatable.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class DatatableComponent implements OnInit, OnDestroy, AfterContentInit {
	@Input() value: RowTable[] = [];
	@Input() hasFilters = false;
	@Input() hasSubFiltersButton = true;
	@Input() filters!: Filter;
	@Input() totalElements = 0;
	@Input() columns: ColumnTable[] = [];
	@Input() hasSelection = false;
	@Input() rowExpandMode: RowExpandMode = "multiple";
	@Input() selectList!: DataListNgSelect[];
	@Input() pageSize = 5;
	@Input() showPagination = true;

	@Output() selectionChange: EventEmitter<unknown[]> = new EventEmitter<
		unknown[]
	>();
	@Output() changeEvent = new EventEmitter<HttpParamsConfig>();

	@ContentChildren(AutomataTemplateDirective)
	templates!: QueryList<AutomataTemplateDirective>;
	tableHeader!: TemplateRef<unknown>;
	tableBody!: TemplateRef<unknown>;
	tableRowExpansion!: TemplateRef<unknown>;
	tableFilters!: TemplateRef<unknown>;
	tableFooter!: TemplateRef<unknown>;

	httpParams: HttpParamsConfig = {};
	showFilters = false;
	expandedRows: RowTable[] = [];
	selection: RowTable[] = [];
	actualSortedFieldStatus: SortedField = "desc";
	actualSortedFieldProps = "";

	protected bsDateConfig: Partial<BsDatepickerConfig> = {
		isAnimated: true,
		containerClass: "theme-dark-blue",
		adaptivePosition: true,
		showWeekNumbers: false
	};

	private _onDestroy = new Subject<boolean>();

	constructor(
		private localeService: BsLocaleService,
		@Optional() private readonly tableCheckboxService: TableCheckboxService
	) {}

	ngOnInit(): void {
		this.localeService.use("pt-br");
		this.initializeHttpParams();
		this.initSelectionRowMode();
	}

	ngOnDestroy(): void {
		this._onDestroy.next(true);
	}

	ngAfterContentInit(): void {
		this.setCustomTemplates();
	}

	private initSelectionRowMode(): void {
		if (this.hasSelection && !this.tableCheckboxService) {
			throw new Error(
				"É preciso prover uma instância de TableCheckboxService para a seleção funcionar corretamente."
			);
		}
		if (this.tableCheckboxService) {
			this.isThereAnySelected();
			this.activateSelection();
			this.checkUncheckAll();
		}
	}

	private setCustomTemplates(): void {
		this.templates.forEach((item) => {
			switch (item.getType() as DatatableTemplate) {
				case "header":
					this.tableHeader = item.template;
					break;
				case "body":
					this.tableBody = item.template;
					break;
				case "rowexpansion":
					this.tableRowExpansion = item.template;
					break;
				case "filters":
					this.tableFilters = item.template;
					break;
				case "footer":
					this.tableFooter = item.template;
					break;
			}
		});
	}

	private initializeHttpParams(): void {
		this.httpParams = {
			page: 0,
			size: this.pageSize
		};
	}

	private isThereAnySelected(): void {
		if (this.value.length > 0) {
			const rowsTable = this.value;
			const addToSelection: RowTable[] = [];
			rowsTable.forEach((element) => {
				if (element.isChecked) {
					addToSelection.push(element);
				}
			});
			this.selection = [...addToSelection];
			this.tableCheckboxService.setSelection(this.selection);
		}
	}

	private checkUncheckAll(): void {
		this.tableCheckboxService.checkAll$
			.pipe(takeUntil(this._onDestroy))
			.subscribe((status) => {
				this.value.forEach((row) => (row.isChecked = status));
				this.selection = status ? [...this.value] : [];
				this.tableCheckboxService.setSelection(this.selection);
			});
	}

	private activateSelection(): void {
		this.tableCheckboxService.selectedElement$
			.pipe(takeUntil(this._onDestroy))
			.subscribe((newElement) => {
				const rowTable = newElement as RowTable;
				if (this.selection.indexOf(rowTable) === -1) {
					this.selection = [...this.selection, rowTable];
					this.tableCheckboxService.setSelection(this.selection);
					if (this.selection.length === this.value.length) {
						this.tableCheckboxService.setCheckAllDt(true);
					}
					return;
				}
				this.selection = this.selection.filter(
					(selectedItem) => selectedItem.id !== rowTable.id
				);
				this.tableCheckboxService.setSelection(this.selection);
				this.tableCheckboxService.setCheckAllDt(false);
			});
	}

	pageChanged(event: PageChangedEvent): void {
		this.httpParams.page = event.page - 1;
		this.changeEvent.emit(this.httpParams);
	}

	orderColumnStatus(prop: string): void {
		if (prop === this.actualSortedFieldProps) {
			if (this.actualSortedFieldStatus === "asc") {
				this.actualSortedFieldStatus = "desc";
				return;
			}
			if (this.actualSortedFieldStatus === "desc") {
				this.actualSortedFieldStatus = "asc";
				return;
			}
			return;
		}
		this.actualSortedFieldStatus = "asc";
	}

	orderBy(prop: string): void {
		this.actualSortedFieldProps = prop;
		this.columns.forEach((column) => {
			if (column.prop === prop) {
				column.active = true;
			} else {
				column.active = false;
			}
		});
		this.orderColumnStatus(prop);
		this.httpParams.sort = `${prop},${this.actualSortedFieldStatus}`;
		this.changeEvent.emit(this.httpParams);
	}

	isSortedColumnAsc(prop: string): boolean {
		return (
			prop === this.actualSortedFieldProps &&
			this.actualSortedFieldStatus === "asc"
		);
	}

	isSortedColumnDesc(prop: string): boolean {
		return (
			prop === this.actualSortedFieldProps &&
			this.actualSortedFieldStatus === "desc"
		);
	}

	getRestrictionType(restriction: Restriction): string {
		switch (restriction.editType) {
			case "text_search":
				return "text";
			default:
				return "text";
		}
	}

	updateFilter(
		restriction: string,
		restrictionValue: string,
		emitEvent = true
	): void {
		this.httpParams = {
			page: 0,
			size: this.pageSize,
			fields: [{ key: restriction, value: restrictionValue }]
		};
		if (emitEvent) {
			this.changeEvent.emit(this.httpParams);
		}
	}

	toggleShowFilters(): void {
		this.showFilters = !this.showFilters;
	}

	toggleRow(rowData: RowTable): void {
		const rowIndex = this.expandedRows.findIndex(
			(item) => item.id === rowData.id
		);
		if (this.rowExpandMode === "single") {
			if (this.expandedRows.length > 0) {
				this.expandedRows = [];
				if (rowIndex > -1) {
					return;
				}
			}
			this.expandedRows.push(rowData);
		}

		if (this.rowExpandMode === "multiple") {
			if (rowIndex > -1) {
				this.expandedRows.splice(rowIndex, 1);
				return;
			}
			this.expandedRows.push(rowData);
		}
	}

	isRowExpanded(rowData: RowTable): boolean {
		return !!this.expandedRows.find((element) => rowData.id === element.id);
	}

	filterRestrictions(restrictions: Array<Restriction>): Array<Restriction> {
		const restrictionsFilter: Array<Restriction> = restrictions.filter(
			(item) => {
				return item.editType !== "hidden";
			}
		);

		return restrictionsFilter;
	}

	convertToString(value: unknown): string {
		return String(value);
	}
}
